/**
 * Presentational browser-phone panel.
 * Transport (Twilio Voice SDK) stays outside this file. Pass handlers; a missing
 * handler hides that control. Optional initialView ('keypad' | 'devices') only
 * seeds which drawer starts open — omit it in production.
 *
 * Styles: public/admin/css/softphone.css
 * Notes during a call: pass api, callSid, and optionally user, timezone, toast.
 */
import { NotesPanel } from './lead-notes.js';
import { DispositionPicker } from './dispositions.js';

const { useState, useEffect, useRef, useMemo } = React;

var STATUS_META = {
  idle: { label: 'Ready' },
  incoming: { label: 'Incoming handoff' },
  connecting: { label: 'Connecting…' },
  connected: { label: 'Live' },
  on_hold: { label: 'On hold' },
  ended: { label: 'Call ended' },
  error: { label: 'Call failed' }
};

var DTMF_KEYS = [
  { d: '1', letters: '' },
  { d: '2', letters: 'ABC' },
  { d: '3', letters: 'DEF' },
  { d: '4', letters: 'GHI' },
  { d: '5', letters: 'JKL' },
  { d: '6', letters: 'MNO' },
  { d: '7', letters: 'PQRS' },
  { d: '8', letters: 'TUV' },
  { d: '9', letters: 'WXYZ' },
  { d: '*', letters: '' },
  { d: '0', letters: '+' },
  { d: '#', letters: '' }
];

function isFn(fn) {
  return typeof fn === 'function';
}

function safeStr(value) {
  if (value == null) return '';
  return String(value);
}

function clamp01(value) {
  var n = Number(value);
  if (!isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > 1) return 1;
  return n;
}

function formatUsPhone(phone) {
  var raw = safeStr(phone).trim();
  if (!raw) return '';
  var digits = raw.replace(/\D/g, '');
  var national = digits;
  if (digits.length === 11 && digits.charAt(0) === '1') national = digits.slice(1);
  if (national.length === 10) {
    return '(' + national.slice(0, 3) + ') ' + national.slice(3, 6) + '-' + national.slice(6);
  }
  return raw;
}

function initialsFrom(name, company) {
  var source = safeStr(name).trim() || safeStr(company).trim();
  if (!source) return '?';
  var parts = source.split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

function pad2(n) {
  return n < 10 ? '0' + n : String(n);
}

function formatClock(ms) {
  var total = Math.max(0, Math.floor(ms / 1000));
  var s = total % 60;
  var m = Math.floor(total / 60) % 60;
  var h = Math.floor(total / 3600);
  if (h > 0) return h + ':' + pad2(m) + ':' + pad2(s);
  return pad2(m) + ':' + pad2(s);
}

function formatSpoken(ms) {
  var total = Math.max(0, Math.floor(Number(ms) / 1000));
  var s = total % 60;
  var m = Math.floor(total / 60) % 60;
  var h = Math.floor(total / 3600);
  var parts = [];
  if (h) parts.push(h + (h === 1 ? ' hour' : ' hours'));
  if (m) parts.push(m + (m === 1 ? ' minute' : ' minutes'));
  if (s || !parts.length) parts.push(s + (s === 1 ? ' second' : ' seconds'));
  return parts.join(' ');
}

function formatTs(ts) {
  if (ts == null || ts === '') return '';
  var d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  var h = d.getHours();
  var m = d.getMinutes();
  var suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12;
  if (h === 0) h = 12;
  return h + ':' + pad2(m) + ' ' + suffix;
}

function interestMeta(value) {
  var raw = safeStr(value).trim();
  if (!raw) return null;
  var key = raw.toLowerCase();
  if (key === 'hot' || key === 'warm' || key === 'cold') {
    return { key: key, label: key.charAt(0).toUpperCase() + key.slice(1) };
  }
  return { key: 'other', label: raw };
}

function roleMeta(role) {
  if (role === 'agent') return { label: 'You', cls: 'is-you' };
  if (role === 'assistant') return { label: 'AI', cls: 'is-ai' };
  if (role === 'user') return { label: 'Caller', cls: 'is-caller' };
  return { label: 'Caller', cls: 'is-caller' };
}

function digitName(d) {
  if (d === '*') return 'Star';
  if (d === '#') return 'Pound';
  return 'Digit ' + d;
}

function Icon(props) {
  var name = props.name;
  var size = props.size || 22;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={props.stroke || 1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {iconShape(name)}
    </svg>
  );
}

function iconShape(name) {
  if (name === 'phoneOff') {
    return (
      <path
        fill="currentColor"
        stroke="none"
        d="M12 9c-1.6 0-3.15.25-4.6.72v3.1c0 .39-.23.74-.56.9-.98.49-1.87 1.12-2.66 1.85-.18.18-.43.28-.7.28-.28 0-.53-.11-.71-.29L.29 13.08c-.18-.17-.29-.42-.29-.7 0-.28.11-.53.29-.71C3.34 8.78 7.46 7 12 7s8.66 1.78 11.71 4.67c.18.18.29.43.29.71 0 .28-.11.53-.29.71l-2.48 2.48c-.18.18-.43.29-.71.29-.27 0-.52-.11-.7-.28-.79-.74-1.69-1.36-2.67-1.85-.33-.16-.56-.5-.56-.9v-3.1C15.15 9.25 13.6 9 12 9z"
      />
    );
  }
  if (name === 'mic') {
    return (
      <>
        <rect x="9" y="2.5" width="6" height="11" rx="3" />
        <path d="M6 11a6 6 0 0 0 12 0" />
        <path d="M12 17v3.5" />
        <path d="M8.5 20.5h7" />
      </>
    );
  }
  if (name === 'micOff') {
    return (
      <>
        <rect x="9" y="2.5" width="6" height="11" rx="3" />
        <path d="M6 11a6 6 0 0 0 12 0" />
        <path d="M12 17v3.5" />
        <path d="M8.5 20.5h7" />
        <path d="M4 4l16 16" />
      </>
    );
  }
  if (name === 'pause') {
    return (
      <>
        <rect x="6" y="4" width="4" height="16" rx="1" />
        <rect x="14" y="4" width="4" height="16" rx="1" />
      </>
    );
  }
  if (name === 'keypad') {
    return (
      <>
        <circle cx="6" cy="6" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="12" cy="6" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="18" cy="6" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="6" cy="12" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="18" cy="12" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="6" cy="18" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="12" cy="18" r="1.15" fill="currentColor" stroke="none" />
        <circle cx="18" cy="18" r="1.15" fill="currentColor" stroke="none" />
      </>
    );
  }
  if (name === 'speaker') {
    return (
      <>
        <path d="M11 5 6 9H3v6h3l5 4V5z" />
        <path d="M15.5 8.5a5 5 0 0 1 0 7" />
        <path d="M18 6a8.5 8.5 0 0 1 0 12" />
      </>
    );
  }
  if (name === 'headphones') {
    return (
      <>
        <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
        <path d="M3 16.5A2.5 2.5 0 0 0 5.5 19H7v-6H5.5A2.5 2.5 0 0 0 3 15.5z" />
        <path d="M21 16.5A2.5 2.5 0 0 1 18.5 19H17v-6h1.5A2.5 2.5 0 0 1 21 15.5z" />
      </>
    );
  }
  if (name === 'smartphone') {
    return (
      <>
        <rect x="7" y="2" width="10" height="20" rx="2" />
        <path d="M11 18h2" />
      </>
    );
  }
  if (name === 'chevronDown') {
    return <path d="m6 9 6 6 6-6" />;
  }
  if (name === 'chevronUp') {
    return <path d="m6 15 6-6 6 6" />;
  }
  if (name === 'x') {
    return (
      <>
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
      </>
    );
  }
  if (name === 'note') {
    return (
      <>
        <path d="M7 3h7l5 5v13a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
        <path d="M14 3v5h5" />
        <path d="M8 13h8" />
        <path d="M8 17h5" />
      </>
    );
  }
  if (name === 'alert') {
    return (
      <>
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h16.9a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
      </>
    );
  }
  return (
    <path
      fill="currentColor"
      stroke="none"
      d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"
    />
  );
}

function deviceList(list, currentId) {
  var items = [];
  var source = Array.isArray(list) ? list : [];
  var i;
  for (i = 0; i < source.length; i++) {
    var item = source[i];
    if (!item || item.id == null || safeStr(item.id) === '') continue;
    items.push({
      id: safeStr(item.id),
      label: safeStr(item.label).trim() || safeStr(item.id)
    });
  }
  if (currentId && !items.some(function (d) { return d.id === currentId; })) {
    items.unshift({ id: currentId, label: 'Current device' });
  }
  return items;
}

function Meter(props) {
  var level = clamp01(props.level);
  var shown = props.muted ? 0 : level;
  var pct = Math.round(shown * 100);
  var hot = !props.muted && shown >= 0.85;
  return (
    <div className={'sp-meter-row' + (props.muted ? ' is-muted' : '')}>
      <span className="sp-meter-label">{props.label}</span>
      <div
        className="sp-meter-track"
        role="meter"
        aria-label={props.label + ' level'}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-valuetext={props.muted ? 'Muted' : pct + ' percent'}
      >
        <div
          className={'sp-meter-fill' + (props.caller ? ' is-caller' : '') + (hot ? ' is-hot' : '')}
          style={{ width: pct + '%' }}
        />
      </div>
      <span className="sp-meter-value">{props.muted ? 'Muted' : pct + '%'}</span>
    </div>
  );
}

function TranscriptList(props) {
  var lines = props.lines;
  if (!lines.length) {
    return <p className="sp-empty">No live transcript yet.</p>;
  }
  return (
    <div
      className={'sp-transcript' + (props.compact ? ' is-incoming' : '')}
      ref={props.scrollerRef}
      onScroll={props.onScroll}
      role="log"
      aria-label={props.label}
      tabIndex={0}
    >
      {lines.map(function (line, index) {
        var meta = roleMeta(line && line.role);
        var when = formatTs(line && line.ts);
        var text = line && line.content != null ? safeStr(line.content) : '';
        var key = line && line.id != null ? safeStr(line.id) : 'line-' + index;
        return (
          <div key={key} className={'sp-line ' + meta.cls}>
            <span className="sp-who">{when ? meta.label + ' · ' + when : meta.label}</span>
            <div className="sp-bubble">{text}</div>
          </div>
        );
      })}
      <div ref={props.endRef} />
    </div>
  );
}

export function SoftphonePanel(props) {
  props = props || {};
  var status = STATUS_META[props.status] ? props.status : 'idle';
  var mode = props.mode === 'phone' || props.mode === 'listen' ? props.mode : 'browser';
  var caller = props.caller && typeof props.caller === 'object' ? props.caller : {};
  var context = props.context && typeof props.context === 'object' ? props.context : {};
  var devices = props.devices && typeof props.devices === 'object' ? props.devices : null;
  var transcript = Array.isArray(props.transcript) ? props.transcript : [];
  var minimized = !!props.minimized;
  var muted = !!props.muted;
  var held = !!props.onHold || status === 'on_hold';
  var errorText = safeStr(props.error).trim();
  var startedAt = typeof props.startedAt === 'number' && isFinite(props.startedAt) ? props.startedAt : null;

  var onAccept = isFn(props.onAccept) ? props.onAccept : null;
  var onDecline = isFn(props.onDecline) ? props.onDecline : null;
  var onListen = isFn(props.onListen) ? props.onListen : null;
  var onJoinPhone = isFn(props.onJoinPhone) ? props.onJoinPhone : null;
  var onToggleMute = isFn(props.onToggleMute) ? props.onToggleMute : null;
  var onToggleHold = isFn(props.onToggleHold) ? props.onToggleHold : null;
  var onHangup = isFn(props.onHangup) ? props.onHangup : null;
  var onDigit = isFn(props.onDigit) ? props.onDigit : null;
  var onSelectInput = isFn(props.onSelectInput) ? props.onSelectInput : null;
  var onSelectOutput = isFn(props.onSelectOutput) ? props.onSelectOutput : null;
  var onMinimize = isFn(props.onMinimize) ? props.onMinimize : null;
  var onRestore = isFn(props.onRestore) ? props.onRestore : null;
  var onClose = isFn(props.onClose) ? props.onClose : null;

  var liveCall = status === 'connected' || status === 'on_hold';
  var showTimer = liveCall || status === 'ended';

  var [now, setNow] = useState(function () { return Date.now(); });
  var [panel, setPanel] = useState(function () {
    if (!liveCall) return null;
    if (props.initialView === 'keypad' || props.initialView === 'devices') return props.initialView;
    return null;
  });
  var [summaryOpen, setSummaryOpen] = useState(false);
  var [notesOpen, setNotesOpen] = useState(false);
  var [pressedKey, setPressedKey] = useState('');
  var [dialed, setDialed] = useState('');

  var rootRef = useRef(null);
  var transcriptRef = useRef(null);
  var endRef = useRef(null);
  var pinRef = useRef(true);
  var endedMsRef = useRef(null);
  var apiRef = useRef({});
  var uid = useMemo(function () {
    return 'sp' + Math.random().toString(36).slice(2, 9);
  }, []);

  useEffect(function () {
    if (!liveCall) return undefined;
    var id = setInterval(function () { setNow(Date.now()); }, 1000);
    return function () { clearInterval(id); };
  }, [liveCall]);

  useEffect(function () {
    if (status === 'ended') {
      if (endedMsRef.current == null && startedAt) {
        endedMsRef.current = Math.max(0, Date.now() - startedAt);
      }
    } else {
      endedMsRef.current = null;
    }
  }, [status, startedAt]);

  useEffect(function () {
    if (status !== 'connected' && status !== 'on_hold') setPanel(null);
  }, [status]);

  useEffect(function () {
    var el = transcriptRef.current;
    if (!el || !pinRef.current) return undefined;
    function pin() {
      el.scrollTop = el.scrollHeight;
    }
    pin();
    var raf = window.requestAnimationFrame(pin);
    return function () { window.cancelAnimationFrame(raf); };
  }, [transcript, status, minimized, panel]);

  var view = useMemo(function () {
    var name = safeStr(caller.name).trim();
    var company = safeStr(caller.company).trim();
    var phone = formatUsPhone(caller.phone);
    var displayName = name || company || phone || 'Unknown caller';
    var subBits = [];
    if (company && company !== displayName) subBits.push(company);
    if (phone && phone !== displayName) subBits.push(phone);
    var leadRaw = caller.leadId;
    var leadLabel = leadRaw != null && safeStr(leadRaw).trim() !== '' ? 'Lead #' + safeStr(leadRaw).trim() : '';
    var meta = STATUS_META[status] || STATUS_META.idle;
    var interest = interestMeta(context.interest);
    var summary = safeStr(context.summary).trim();
    var reason = safeStr(context.reason).trim();
    var inputId = devices && devices.inputId != null ? safeStr(devices.inputId) : '';
    var outputId = devices && devices.outputId != null ? safeStr(devices.outputId) : '';
    return {
      displayName: displayName,
      initials: initialsFrom(name, company),
      subline: subBits.join(' · '),
      leadLabel: leadLabel,
      statusLabel: meta.label,
      interest: interest,
      summary: summary,
      reason: reason,
      inputs: deviceList(devices && devices.inputs, inputId),
      outputs: deviceList(devices && devices.outputs, outputId),
      inputId: inputId,
      outputId: outputId
    };
  }, [caller, context, devices, status]);

  var elapsedMs = null;
  if (startedAt && liveCall) elapsedMs = Math.max(0, now - startedAt);
  if (startedAt && status === 'ended') {
    elapsedMs = endedMsRef.current != null ? endedMsRef.current : Math.max(0, Date.now() - startedAt);
  }
  var clock = elapsedMs == null ? '' : formatClock(elapsedMs);

  var announce = useMemo(function () {
    var parts = [view.statusLabel];
    if (view.displayName && view.displayName !== 'Unknown caller') parts.push(view.displayName);
    if (status === 'connected' && mode === 'listen') parts.push('Listen only');
    if (status === 'connected' && mode === 'phone') parts.push('On your phone');
    if (status === 'error') parts.push(errorText || 'The call could not be completed.');
    if (status === 'ended' && elapsedMs != null) parts.push('Duration ' + formatSpoken(elapsedMs));
    return parts.join('. ');
  }, [view.statusLabel, view.displayName, status, mode, errorText, status === 'ended' ? elapsedMs : null]);

  apiRef.current = {
    status: status,
    liveCall: liveCall,
    minimized: minimized,
    panel: panel,
    onToggleMute: onToggleMute,
    onMinimize: onMinimize,
    setPanel: setPanel
  };

  useEffect(function () {
    function shortcutAllowed() {
      var root = rootRef.current;
      if (!root) return false;
      var active = document.activeElement;
      var tag = active && active.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (active && active.isContentEditable)) {
        return false;
      }
      if (active && root.contains(active)) return true;
      var docks = document.querySelectorAll('.sp-dock');
      return docks.length === 1 && docks[0] === root;
    }

    function onKey(e) {
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey) return;
      if (!shortcutAllowed()) return;
      var api = apiRef.current;
      if ((e.key === 'm' || e.key === 'M') && api.liveCall && api.onToggleMute) {
        e.preventDefault();
        api.onToggleMute();
        return;
      }
      if (e.key === 'Escape') {
        if (api.panel) {
          e.preventDefault();
          api.setPanel(null);
          return;
        }
        if (!api.minimized && api.onMinimize && api.status !== 'ended' && api.status !== 'idle' && api.status !== 'error') {
          e.preventDefault();
          api.onMinimize();
        }
      }
    }

    document.addEventListener('keydown', onKey);
    return function () { document.removeEventListener('keydown', onKey); };
  }, []);

  function onTranscriptScroll() {
    var el = transcriptRef.current;
    if (!el) return;
    pinRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  }

  function togglePanel(next) {
    setPanel(function (cur) { return cur === next ? null : next; });
  }

  function pressDigit(d) {
    setPressedKey(d);
    setDialed(function (prev) {
      var next = (prev + d).slice(-18);
      return next;
    });
    if (onDigit) onDigit(d);
    window.setTimeout(function () {
      setPressedKey(function (cur) { return cur === d ? '' : cur; });
    }, 160);
  }

  var dockClass = 'sp-dock sp-status-' + status + (minimized ? ' is-min' : '');
  var showClose = !!onClose && (status === 'ended' || status === 'error' || status === 'idle');
  var showMinimize = !!onMinimize && !showClose;
  var modeLabel = mode === 'listen' ? 'Listen only' : mode === 'phone' ? 'On your phone' : '';
  var micLevel = clamp01(props.inputLevel);
  var speakerLevel = clamp01(props.outputLevel);
  var sectionLabel = status === 'incoming' ? 'So far on this call' : 'Live transcript';

  var pillSub = clock;
  if (muted && liveCall) pillSub = pillSub ? pillSub + ' · Muted' : 'Muted';

  var pillLabel = view.statusLabel + (view.displayName && view.displayName !== 'Unknown caller' ? ', ' + view.displayName : '');

  var body = null;
  if (status === 'incoming') {
    body = (
      <>
        {view.reason ? (
          <div className="sp-context">
            <span className="sp-kicker">Why you’re here</span>
            <p className="sp-reason">{view.reason}</p>
          </div>
        ) : null}
        {(onAccept || onDecline) ? (
          <div className="sp-answer-row">
            {onDecline ? (
              <button type="button" className="sp-answer" onClick={onDecline}>
                <span className="sp-answer-circle sp-fill-danger"><Icon name="phoneOff" size={28} stroke={2} /></span>
                <span className="sp-answer-label">Decline</span>
              </button>
            ) : null}
            {onAccept ? (
              <button type="button" className="sp-answer" onClick={onAccept}>
                <span className="sp-answer-circle sp-fill-success"><Icon name="phone" size={28} stroke={2} /></span>
                <span className="sp-answer-label">Accept</span>
              </button>
            ) : null}
          </div>
        ) : null}
        {(onListen || onJoinPhone) ? (
          <div className="sp-secondary-row">
            {onListen ? (
              <button type="button" className="sp-secondary" onClick={onListen}>
                <Icon name="headphones" size={16} />
                Listen first
              </button>
            ) : null}
            {onJoinPhone ? (
              <button type="button" className="sp-secondary" onClick={onJoinPhone}>
                <Icon name="smartphone" size={16} />
                Answer on my phone
              </button>
            ) : null}
          </div>
        ) : null}
      </>
    );
  } else if (status === 'connecting') {
    var connectCopy = mode === 'phone'
      ? 'Calling your phone…'
      : mode === 'listen'
        ? 'Opening listen-only audio. Your mic stays off.'
        : 'Connecting your browser…';
    var cancel = onHangup || onDecline;
    body = (
      <div className="sp-connect">
        <div className="sp-spinner" aria-hidden="true" />
        <p>{connectCopy}</p>
        {cancel ? (
          <button type="button" className="sp-secondary" onClick={cancel}>Cancel</button>
        ) : null}
      </div>
    );
  } else if (liveCall) {
    var showDevices = !!(onSelectInput || onSelectOutput);
    var showKeypad = !!onDigit;
    body = (
      <>
        {status === 'on_hold' ? <div className="sp-holdbar">Call is on hold</div> : null}
        {mode === 'listen' ? (
          <div className="sp-note">
            <p>Listening with your mic off. Join to take over the call, then unmute to speak.</p>
          </div>
        ) : null}
        {mode === 'phone' ? (
          <div className="sp-note">
            <p>This call is on your phone.</p>
          </div>
        ) : null}
        {mode === 'listen' && onAccept ? (
          <button type="button" className="sp-join" onClick={onAccept}>
            <Icon name="phone" size={18} />
            Join call
          </button>
        ) : null}
        {mode === 'listen' && onJoinPhone ? (
          <div className="sp-secondary-row">
            <button type="button" className="sp-secondary" onClick={onJoinPhone}>
              <Icon name="smartphone" size={16} />
              Answer on my phone
            </button>
          </div>
        ) : null}
        <div className="sp-meters">
          <Meter label="Mic" level={micLevel} muted={muted || mode === 'listen'} />
          <Meter label="Caller" level={speakerLevel} caller />
        </div>
        {(onToggleMute || onToggleHold || showKeypad || showDevices || onHangup) ? (
          <div className="sp-controls" role="group" aria-label="Call controls">
            {onToggleMute ? (
              <button
                type="button"
                className={'sp-ctrl' + (muted ? ' is-muted' : '')}
                aria-pressed={muted}
                aria-keyshortcuts="M"
                onClick={onToggleMute}
              >
                <span className="sp-ctrl-icon"><Icon name={muted ? 'micOff' : 'mic'} size={20} /></span>
                <span className="sp-ctrl-label">{muted ? 'Muted' : 'Mute'}</span>
              </button>
            ) : null}
            {onToggleHold ? (
              <button
                type="button"
                className={'sp-ctrl' + (held ? ' is-hold' : '')}
                aria-pressed={held}
                onClick={onToggleHold}
              >
                <span className="sp-ctrl-icon"><Icon name="pause" size={18} /></span>
                <span className="sp-ctrl-label">{held ? 'Holding' : 'Hold'}</span>
              </button>
            ) : null}
            {showKeypad ? (
              <button
                type="button"
                className={'sp-ctrl' + (panel === 'keypad' ? ' is-open' : '')}
                aria-expanded={panel === 'keypad'}
                aria-controls={uid + '-keypad'}
                onClick={function () { togglePanel('keypad'); }}
              >
                <span className="sp-ctrl-icon"><Icon name="keypad" size={20} /></span>
                <span className="sp-ctrl-label">Keypad</span>
              </button>
            ) : null}
            {showDevices ? (
              <button
                type="button"
                className={'sp-ctrl' + (panel === 'devices' ? ' is-open' : '')}
                aria-expanded={panel === 'devices'}
                aria-controls={uid + '-devices'}
                onClick={function () { togglePanel('devices'); }}
              >
                <span className="sp-ctrl-icon"><Icon name="speaker" size={20} /></span>
                <span className="sp-ctrl-label">Devices</span>
              </button>
            ) : null}
            {onHangup ? (
              <button type="button" className="sp-ctrl sp-ctrl-end" onClick={onHangup}>
                <span className="sp-ctrl-icon sp-fill-danger"><Icon name="phoneOff" size={20} stroke={2} /></span>
                <span className="sp-ctrl-label">End</span>
              </button>
            ) : null}
          </div>
        ) : null}
        {panel === 'keypad' && showKeypad ? (
          <div className="sp-keypad" id={uid + '-keypad'}>
            <p className={'sp-dial' + (dialed ? '' : ' is-empty')}>{dialed || 'DTMF keypad'}</p>
            {DTMF_KEYS.map(function (key) {
              return (
                <button
                  key={key.d}
                  type="button"
                  className={'sp-key' + (pressedKey === key.d ? ' is-pressed' : '')}
                  aria-label={digitName(key.d)}
                  onClick={function () { pressDigit(key.d); }}
                >
                  <b>{key.d}</b>
                  <small aria-hidden="true">{key.letters || ' '}</small>
                </button>
              );
            })}
          </div>
        ) : null}
        {panel === 'devices' && showDevices ? (
          <div className="sp-devices" id={uid + '-devices'}>
            {onSelectInput ? (
              <label className="sp-field">
                Microphone
                <select
                  value={view.inputId || ''}
                  onChange={function (e) { onSelectInput(e.target.value); }}
                >
                  {!view.inputId ? <option value="">Select microphone</option> : null}
                  {view.inputs.map(function (item) {
                    return <option key={item.id} value={item.id}>{item.label}</option>;
                  })}
                </select>
              </label>
            ) : null}
            {onSelectOutput ? (
              <label className="sp-field">
                Speaker
                <select
                  value={view.outputId || ''}
                  onChange={function (e) { onSelectOutput(e.target.value); }}
                >
                  {!view.outputId ? <option value="">Select speaker</option> : null}
                  {view.outputs.map(function (item) {
                    return <option key={item.id} value={item.id}>{item.label}</option>;
                  })}
                </select>
              </label>
            ) : null}
            {!view.inputs.length && !view.outputs.length ? (
              <p className="sp-devices-empty">No audio devices reported yet.</p>
            ) : null}
          </div>
        ) : null}
      </>
    );
  } else if (status === 'error') {
    body = (
      <>
        <div className="sp-banner sp-banner-error" role="alert">
          <strong className="sp-banner-title">
            <Icon name="alert" size={14} /> Call failed
          </strong>
          <p>{errorText || 'The call could not be completed.'}</p>
          <p className="sp-hint">Check your microphone permission and connection, then try the call again.</p>
        </div>
        {(onAccept || onClose) ? (
          <div className="sp-action-row">
            {onAccept ? (
              <button type="button" className="sp-secondary" onClick={onAccept}>Try again</button>
            ) : null}
            {onClose ? (
              <button type="button" className="sp-secondary" onClick={onClose}>Close</button>
            ) : null}
          </div>
        ) : null}
      </>
    );
  } else if (status === 'ended') {
    body = (
      <div className="sp-ended">
        <div className="sp-ended-mark" aria-hidden="true"><Icon name="phoneOff" size={26} /></div>
        <div className="sp-ended-kicker">{clock ? 'Duration' : 'Duration unavailable'}</div>
        {clock ? <div className="sp-ended-time">{clock}</div> : null}
        {isFn(props.api) && props.callSid ? (
          <DispositionPicker compact api={props.api} toast={isFn(props.toast) ? props.toast : null} callSid={props.callSid} timezone={props.timezone} />
        ) : null}
        {onClose ? (
          <button type="button" className="sp-block-btn" onClick={onClose}>Close</button>
        ) : null}
      </div>
    );
  } else {
    body = (
      <div className="sp-idle">
        <div className="sp-idle-mark" aria-hidden="true"><Icon name="phone" size={22} /></div>
        <p className="sp-idle-title">Ready for a handoff</p>
        <p className="sp-idle-copy">When a caller asks for a person, you will see why, how interested they are, and the option to listen or take the call.</p>
      </div>
    );
  }

  var showSummary = !!view.summary && status !== 'idle' && status !== 'connecting';
  var showTranscript = liveCall || ((status === 'incoming' || status === 'ended' || status === 'error') && transcript.length > 0);

  return (
    <div
      className={dockClass}
      ref={rootRef}
      role="region"
      aria-label="Browser phone"
      aria-busy={status === 'connecting' ? true : undefined}
      data-status={status}
      data-mode={mode}
    >
      <div className="sp-live" aria-live="polite" aria-atomic="true">{announce}</div>
      {minimized ? (
        onRestore ? (
          <button type="button" className="sp-pill" onClick={onRestore} aria-label={'Restore ' + pillLabel}>
            <span className="sp-avatar" aria-hidden="true"><span>{view.initials}</span></span>
            <span className="sp-pill-copy">
              <span className="sp-pill-status">{view.statusLabel}</span>
              {pillSub ? <span className="sp-pill-sub">{pillSub}</span> : null}
            </span>
            <span className="sp-pill-chevron" aria-hidden="true"><Icon name="chevronUp" size={18} /></span>
          </button>
        ) : (
          <div className="sp-pill" aria-label={pillLabel}>
            <span className="sp-avatar" aria-hidden="true"><span>{view.initials}</span></span>
            <span className="sp-pill-copy">
              <span className="sp-pill-status">{view.statusLabel}</span>
              {pillSub ? <span className="sp-pill-sub">{pillSub}</span> : null}
            </span>
          </div>
        )
      ) : (
        <>
          <div className="sp-statusbar" aria-hidden="true" />
          <header className="sp-header">
            <div className="sp-avatar" aria-hidden="true"><span>{view.initials}</span></div>
            <div className="sp-id">
              <div className="sp-name" title={view.displayName}>{view.displayName}</div>
              {view.subline ? <div className="sp-sub">{view.subline}</div> : null}
              {view.leadLabel ? <div className="sp-lead">{view.leadLabel}</div> : null}
              <div className="sp-meta-row">
                <span className="sp-chip"><i aria-hidden="true" />{view.statusLabel}</span>
                {view.interest ? (
                  <span className={'sp-interest sp-interest-' + view.interest.key}>
                    <i aria-hidden="true" />
                    {view.interest.label}
                  </span>
                ) : null}
                {modeLabel ? <span className="sp-mode">{modeLabel}</span> : null}
              </div>
            </div>
            <div className="sp-header-side">
              {showTimer && clock ? <div className="sp-timer"><span className="sp-sr">Call duration </span>{clock}</div> : null}
              {(showMinimize || showClose) ? (
                <div className="sp-header-actions">
                  {showMinimize ? (
                    <button
                      type="button"
                      className="sp-icon-btn"
                      aria-label="Minimize call panel"
                      aria-keyshortcuts="Escape"
                      onClick={onMinimize}
                    >
                      <Icon name="chevronDown" size={18} />
                    </button>
                  ) : null}
                  {showClose ? (
                    <button type="button" className="sp-icon-btn" aria-label="Close call panel" onClick={onClose}>
                      <Icon name="x" size={18} />
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
          </header>
          <div className="sp-dock-body">
            {body}
            {showSummary ? (
              <>
                <button
                  type="button"
                  className={'sp-summary' + (summaryOpen ? ' is-open' : '')}
                  aria-expanded={summaryOpen}
                  onClick={function () { setSummaryOpen(function (open) { return !open; }); }}
                >
                  <span>AI summary</span>
                  <span className="sp-summary-chevron" aria-hidden="true"><Icon name="chevronDown" size={16} /></span>
                </button>
                {summaryOpen ? <div className="sp-summary-body">{view.summary}</div> : null}
              </>
            ) : null}
            {status !== 'idle' ? (
              <div className="sp-tabs" role="tablist" aria-label="Call details">
                <button
                  type="button"
                  role="tab"
                  className={'sp-tab' + (notesOpen ? '' : ' is-on')}
                  aria-selected={notesOpen ? 'false' : 'true'}
                  onClick={function () { setNotesOpen(false); }}
                >
                  Transcript
                </button>
                <button
                  type="button"
                  role="tab"
                  className={'sp-tab' + (notesOpen ? ' is-on' : '')}
                  aria-selected={notesOpen ? 'true' : 'false'}
                  onClick={function () { setNotesOpen(true); }}
                >
                  <Icon name="note" size={14} />
                  Notes
                </button>
              </div>
            ) : null}
            {notesOpen && status !== 'idle' ? (
              <div className="sp-notes">
                <NotesPanel
                  api={isFn(props.api) ? props.api : null}
                  toast={isFn(props.toast) ? props.toast : null}
                  user={props.user && typeof props.user === 'object' ? props.user : null}
                  leadId={caller.leadId}
                  callSid={props.callSid}
                  timezone={props.timezone}
                  compact
                />
              </div>
            ) : null}
            {!notesOpen && showTranscript ? (
              <>
                <div className="sp-section-label">{sectionLabel}</div>
                <TranscriptList
                  lines={transcript}
                  label={sectionLabel}
                  compact={status === 'incoming'}
                  scrollerRef={transcriptRef}
                  endRef={endRef}
                  onScroll={onTranscriptScroll}
                />
              </>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

var SAMPLE_DEVICES = {
  inputs: [
    { id: 'mic-mac', label: 'MacBook Pro Microphone' },
    { id: 'mic-headset', label: 'USB Headset Mic' }
  ],
  outputs: [
    { id: 'spk-mac', label: 'MacBook Pro Speakers' },
    { id: 'spk-headset', label: 'USB Headset' }
  ],
  inputId: 'mic-headset',
  outputId: 'spk-headset'
};

var SAMPLE_TRANSCRIPT = [
  { id: 't1', role: 'user', content: 'Hi, I was hoping to talk with someone about implant pricing.', ts: Date.now() - 180000 },
  { id: 't2', role: 'assistant', content: 'I can help with that. Are you looking at a single implant or a full arch?', ts: Date.now() - 150000 },
  { id: 't3', role: 'user', content: 'Full arch, and I would like to come in this week if you have anything.', ts: Date.now() - 120000 },
  { id: 't4', role: 'assistant', content: 'You asked for a person. I am bringing a specialist on now.', ts: Date.now() - 90000 },
  { id: 't5', role: 'agent', content: 'Hi Jordan, this is Alex. I can get you on the schedule.', ts: Date.now() - 40000 }
];

function handlers(extra) {
  var base = {
    onAccept: function () {},
    onDecline: function () {},
    onListen: function () {},
    onJoinPhone: function () {},
    onToggleMute: function () {},
    onToggleHold: function () {},
    onHangup: function () {},
    onDigit: function () {},
    onSelectInput: function () {},
    onSelectOutput: function () {},
    onMinimize: function () {},
    onRestore: function () {},
    onClose: function () {}
  };
  if (!extra) return base;
  var key;
  for (key in extra) {
    if (Object.prototype.hasOwnProperty.call(extra, key)) base[key] = extra[key];
  }
  return base;
}

function Story(props) {
  return (
    <figure className="sp-preview-card">
      <figcaption className="sp-preview-label">{props.label}</figcaption>
      <div className="sp-preview-slot">
        <SoftphonePanel {...props.panel} />
      </div>
    </figure>
  );
}

function InteractiveStory() {
  var [status, setStatus] = useState('incoming');
  var [mode, setMode] = useState('browser');
  var [muted, setMuted] = useState(false);
  var [held, setHeld] = useState(false);
  var [minimized, setMinimized] = useState(false);
  var [startedAt, setStartedAt] = useState(null);
  var [inputId, setInputId] = useState('mic-headset');
  var [outputId, setOutputId] = useState('spk-headset');
  var [levels, setLevels] = useState({ inn: 0.22, out: 0.48 });
  var [error, setError] = useState(null);

  useEffect(function () {
    var live = status === 'connected' || status === 'on_hold' || held;
    if (!live || muted) return undefined;
    var id = setInterval(function () {
      setLevels({
        inn: 0.12 + Math.random() * 0.75,
        out: 0.18 + Math.random() * 0.7
      });
    }, 220);
    return function () { clearInterval(id); };
  }, [status, held, muted]);

  function begin(nextMode) {
    setError(null);
    setHeld(false);
    setMode(nextMode);
    setStatus('connecting');
    window.setTimeout(function () {
      setStartedAt(Date.now());
      setStatus('connected');
      if (nextMode === 'listen') setMuted(true);
    }, 900);
  }

  var shownStatus = held && status === 'connected' ? 'on_hold' : status;
  var panelProps = {
    status: shownStatus,
    mode: mode,
    caller: { name: 'Jordan Lee', company: 'Northwind Dental', phone: '+16195550198', leadId: '4821' },
    startedAt: startedAt,
    muted: muted || mode === 'listen',
    onHold: held,
    minimized: minimized,
    inputLevel: levels.inn,
    outputLevel: levels.out,
    transcript: SAMPLE_TRANSCRIPT,
    context: {
      summary: 'Jordan wants a full-arch implant consult this week. Budget was discussed around $18k. Insurance is Delta Dental PPO.',
      reason: 'Caller asked for a human about implant pricing and a same-week appointment.',
      interest: 'hot'
    },
    error: error,
    devices: {
      inputs: SAMPLE_DEVICES.inputs,
      outputs: SAMPLE_DEVICES.outputs,
      inputId: inputId,
      outputId: outputId
    },
    onAccept: function () {
      if (status === 'error') {
        setError(null);
        setStatus('incoming');
        return;
      }
      begin('browser');
    },
    onDecline: function () {
      setHeld(false);
      setStatus('ended');
    },
    onListen: function () { begin('listen'); },
    onJoinPhone: function () { begin('phone'); },
    onToggleMute: function () { setMuted(function (m) { return !m; }); },
    onToggleHold: function () { setHeld(function (h) { return !h; }); },
    onHangup: function () {
      setHeld(false);
      setStatus('ended');
    },
    onDigit: function () {},
    onSelectInput: setInputId,
    onSelectOutput: setOutputId,
    onMinimize: function () { setMinimized(true); },
    onRestore: function () { setMinimized(false); },
    onClose: function () {
      setStatus('idle');
      setMinimized(false);
      setStartedAt(null);
      setHeld(false);
      setMuted(false);
      setMode('browser');
      setError(null);
    }
  };

  return <Story label="Interactive — click through a handoff" panel={panelProps} />;
}

function DevicesStory() {
  var [inputId, setInputId] = useState('mic-headset');
  var [outputId, setOutputId] = useState('spk-mac');
  return (
    <Story
      label="Devices open"
      panel={handlers({
        status: 'connected',
        mode: 'browser',
        caller: { name: 'Elena Vos', company: 'Copper & Rye', phone: '+14155550144', leadId: '5102' },
        startedAt: Date.now() - 86000,
        muted: false,
        onHold: false,
        minimized: false,
        inputLevel: 0.33,
        outputLevel: 0.57,
        transcript: SAMPLE_TRANSCRIPT.slice(0, 3),
        context: {
          summary: 'Elena asked about a private dining buyout on Friday and wants a callback only if the patio is free.',
          interest: 'warm'
        },
        error: null,
        devices: {
          inputs: SAMPLE_DEVICES.inputs,
          outputs: SAMPLE_DEVICES.outputs,
          inputId: inputId,
          outputId: outputId
        },
        initialView: 'devices',
        onSelectInput: setInputId,
        onSelectOutput: setOutputId
      })}
    />
  );
}

export function SoftphonePanelPreview() {
  var [theme, setTheme] = useState(function () {
    var current = document.documentElement.getAttribute('data-theme');
    return current === 'light' ? 'light' : 'dark';
  });

  useEffect(function () {
    document.documentElement.setAttribute('data-theme', theme);
    try { window.localStorage.setItem('sp_preview_theme', theme); } catch (e) {}
  }, [theme]);

  var ago = Date.now();

  return (
    <main className="sp-preview-page">
      <div className="sp-preview-bar">
        <div>
          <h1>Browser phone</h1>
          <p>Agent softphone dock. In the product it floats at the bottom-right and becomes a bottom sheet under 640px.</p>
        </div>
        <button
          type="button"
          className="sp-preview-toggle"
          aria-pressed={theme === 'dark'}
          onClick={function () { setTheme(theme === 'dark' ? 'light' : 'dark'); }}
        >
          {theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
        </button>
      </div>
      <div className="sp-preview-grid">
        <Story
          label="Incoming handoff"
          panel={handlers({
            status: 'incoming',
            mode: 'browser',
            caller: { name: 'Jordan Lee', company: 'Northwind Dental', phone: '+16195550198', leadId: '4821' },
            startedAt: null,
            muted: true,
            onHold: false,
            minimized: false,
            inputLevel: 0,
            outputLevel: 0.2,
            transcript: SAMPLE_TRANSCRIPT.slice(0, 4),
            context: {
              summary: 'Jordan wants a full-arch implant consult this week. Budget was discussed around $18k. Insurance is Delta Dental PPO.',
              reason: 'Caller asked for a human about implant pricing and a same-week appointment.',
              interest: 'hot'
            },
            error: null,
            devices: SAMPLE_DEVICES
          })}
        />
        <Story
          label="Connecting"
          panel={handlers({
            status: 'connecting',
            mode: 'browser',
            caller: { name: 'Priya Shah', company: 'Harbor Logistics', phone: '4155550177', leadId: '3390' },
            startedAt: null,
            muted: true,
            onHold: false,
            minimized: false,
            inputLevel: 0,
            outputLevel: 0,
            transcript: [],
            context: { reason: 'Caller could not find the tracking number in the portal.', interest: 'warm' },
            error: null,
            devices: null
          })}
        />
        <Story
          label="Live"
          panel={handlers({
            status: 'connected',
            mode: 'browser',
            caller: { name: 'Sam Ortiz', company: 'BrightPath Roofing', phone: '+16195550110', leadId: '2204' },
            startedAt: ago - 125000,
            muted: false,
            onHold: false,
            minimized: false,
            inputLevel: 0.46,
            outputLevel: 0.72,
            transcript: SAMPLE_TRANSCRIPT,
            context: {
              summary: 'Sam needs a roof inspection after last week’s storm. Preferred window is Thursday morning. Financing was mentioned but not decided.',
              interest: 'warm'
            },
            error: null,
            devices: SAMPLE_DEVICES
          })}
        />
        <Story
          label="On hold, mic muted"
          panel={handlers({
            status: 'on_hold',
            mode: 'browser',
            caller: { name: 'Elena Vos', company: 'Copper & Rye', phone: '+14155550144', leadId: '5102' },
            startedAt: ago - 86000,
            muted: true,
            onHold: true,
            minimized: false,
            inputLevel: 0.4,
            outputLevel: 0.12,
            transcript: SAMPLE_TRANSCRIPT.slice(0, 3),
            context: { summary: 'Elena asked about a Friday patio buyout for 40 guests.', interest: 'warm' },
            error: null,
            devices: SAMPLE_DEVICES
          })}
        />
        <Story
          label="Listen only"
          panel={handlers({
            status: 'connected',
            mode: 'listen',
            caller: { name: 'Chris Nguyen', company: 'Lumen Pediatrics', phone: '+18585550173', leadId: '1188' },
            startedAt: ago - 54000,
            muted: true,
            onHold: false,
            minimized: false,
            inputLevel: 0,
            outputLevel: 0.64,
            transcript: SAMPLE_TRANSCRIPT.slice(0, 4),
            context: {
              summary: 'Parent wants a same-day sick visit and asked to speak with the front desk.',
              reason: 'AI could not confirm insurance eligibility.',
              interest: 'hot'
            },
            error: null,
            devices: SAMPLE_DEVICES
          })}
        />
        <Story
          label="Keypad open"
          panel={handlers({
            status: 'connected',
            mode: 'browser',
            caller: { name: 'Avery Brooks', company: 'Fieldnote Studio', phone: '+12125550190', leadId: '7741' },
            startedAt: ago - 43000,
            muted: false,
            onHold: false,
            minimized: false,
            inputLevel: 0.28,
            outputLevel: 0.51,
            transcript: SAMPLE_TRANSCRIPT.slice(2, 5),
            context: { summary: 'Avery is returning a missed call about a March retainer.', interest: 'cold' },
            error: null,
            devices: SAMPLE_DEVICES,
            initialView: 'keypad'
          })}
        />
        <DevicesStory />
        <Story
          label="Error"
          panel={handlers({
            status: 'error',
            mode: 'browser',
            caller: { name: 'Morgan Blake', company: 'Northline HVAC', phone: '+16195550142', leadId: '6610' },
            startedAt: ago - 20000,
            muted: false,
            onHold: false,
            minimized: false,
            inputLevel: 0,
            outputLevel: 0,
            transcript: SAMPLE_TRANSCRIPT.slice(0, 2),
            context: { summary: 'Morgan wanted a quote on a system replacement.', interest: 'warm' },
            error: 'Microphone blocked. The browser did not grant microphone access.',
            devices: SAMPLE_DEVICES
          })}
        />
        <Story
          label="Call ended"
          panel={handlers({
            status: 'ended',
            mode: 'browser',
            caller: { name: 'Riley Chen', company: 'Harbor & Co.', phone: '+14155550188', leadId: '904' },
            startedAt: ago - 754000,
            muted: false,
            onHold: false,
            minimized: false,
            inputLevel: 0,
            outputLevel: 0,
            transcript: SAMPLE_TRANSCRIPT,
            context: { summary: 'Riley booked a Thursday 10:30 consult. Send the prep checklist.', interest: 'hot' },
            error: null,
            devices: null
          })}
        />
        <Story
          label="Minimized"
          panel={handlers({
            status: 'connected',
            mode: 'browser',
            caller: { name: 'Avery Brooks', company: 'Fieldnote Studio', phone: '+12125550190', leadId: '7741' },
            startedAt: ago - 43000,
            muted: true,
            onHold: false,
            minimized: true,
            inputLevel: 0,
            outputLevel: 0.4,
            transcript: [],
            context: { interest: 'cold' },
            error: null,
            devices: null
          })}
        />
        <Story
          label="Idle"
          panel={handlers({
            status: 'idle',
            mode: 'browser',
            caller: {},
            startedAt: null,
            muted: false,
            onHold: false,
            minimized: false,
            inputLevel: 0,
            outputLevel: 0,
            transcript: [],
            context: {},
            error: null,
            devices: null,
            onAccept: undefined,
            onDecline: undefined,
            onListen: undefined,
            onJoinPhone: undefined,
            onToggleMute: undefined,
            onToggleHold: undefined,
            onHangup: undefined,
            onDigit: undefined,
            onSelectInput: undefined,
            onSelectOutput: undefined
          })}
        />
        <InteractiveStory />
      </div>
      <section className="sp-preview-sheet">
        <h2>Bottom sheet</h2>
        <p>Under 640px the dock pins to the bottom edge at full width. This frame shows that layout without resizing the window.</p>
        <div className="sp-preview-phoneframe">
          <SoftphonePanel
            {...handlers({
              status: 'incoming',
              mode: 'browser',
              caller: { name: 'Jordan Lee', company: 'Northwind Dental', phone: '+16195550198', leadId: '4821' },
              startedAt: null,
              muted: true,
              onHold: false,
              minimized: false,
              inputLevel: 0,
              outputLevel: 0,
              transcript: SAMPLE_TRANSCRIPT.slice(0, 2),
              context: {
                summary: 'Jordan wants a full-arch implant consult this week.',
                reason: 'Caller asked for a human about implant pricing and a same-week appointment.',
                interest: 'hot'
              },
              error: null,
              devices: SAMPLE_DEVICES
            })}
          />
        </div>
      </section>
    </main>
  );
}
